import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/sharp-solid-svg-icons';
import { useEffect, useMemo } from 'react';
import { SCView } from '../view';
import type { SCAdvanceTableProps } from './types';

interface SCTreeAdvanceTableProps<
  D extends Record<string, any & { children?: D[] }>,
> extends SCAdvanceTableProps<D> {
  Expandable?: boolean;
  DefaultExpandAll?: boolean;
}

const expandFieldName = '__isExpanded';
const treeLevelFieldName = '__treeLevel';

const emptyArray = [];

function useTree<D extends Record<string, any & { children?: D[] }>>(
  props: SCTreeAdvanceTableProps<D>,
) {
  const { Expandable, Expanded = emptyArray, OnExpanded, ...rest } = props;

  useEffect(() => {
    if (!props.DefaultExpandAll) {
      return;
    }

    const arr: (string | number)[] = [];
    function process(rows) {
      rows.forEach((row) => {
        if (row.children) {
          arr.push(row[props.IdField]);
          process(row.children);
        }
      });
    }
    if (props.Rows) {
      process(props.Rows);
    }

    OnExpanded?.(arr);
  }, []);

  const columns = useMemo(() => {
    if (!Expandable) {
      return props.Columns;
    }

    const firstColumn = props.Columns[0];

    function ExpandIcon({ Row }: { Row: D }) {
      if (!Row.children) {
        return null;
      }

      const rId = Row[props.IdField];
      const isExpanded = Expanded.includes(Row[props.IdField]);
      return (
        <SCView
          flex
          itemsCenter
          justifyCenter
          rounded
          w="1.5rem"
          h="1.5rem"
          onClick={() => {
            if (isExpanded) {
              OnExpanded?.(Expanded.filter((id) => id !== rId));
            } else {
              OnExpanded?.([...Expanded, rId]);
            }
          }}
          cursorPointer
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
        </SCView>
      );
    }

    function ExpandIndent({ Row }: { Row: D }) {
      return <span style={{ paddingLeft: `${Row[treeLevelFieldName] * 2}rem` }} />;
    }

    const expandFirstColumnWithIcon = {
      ...firstColumn,
      RenderRow: (renderRowProps) => {
        const { Row } = renderRowProps;

        return (
          <SCView flex itemsCenter>
            <ExpandIndent Row={Row} />
            <SCView mr={2}>
              <ExpandIcon Row={Row} />
            </SCView>
            {firstColumn.RenderRow
              ? firstColumn.RenderRow(renderRowProps)
              : Row[firstColumn.FieldName]}
          </SCView>
        );
      },
    };

    if (props.ExpandColumn === 'first') {
      return [
        expandFirstColumnWithIcon,
        ...props.Columns.slice(1),
      ] as SCAdvanceTableProps<D>['Columns'];
    }

    const expandColumn = {
      Label: 'Tree',
      FieldName: expandFieldName,
      Alignment: 1,
      Width: 26,
      RenderLabel: () => {
        return '';
      },
      RenderRow: ({ Row }) => {
        return <ExpandIcon Row={Row} />;
      },
    };

    const expandFirstColumn = {
      ...firstColumn,
      RenderRow: (renderRowProps) => {
        const { Row } = renderRowProps;

        return (
          <SCView flex itemsCenter>
            <ExpandIndent Row={Row} />
            {firstColumn.RenderRow
              ? firstColumn.RenderRow(renderRowProps)
              : Row[firstColumn.FieldName]}
          </SCView>
        );
      },
    };

    return [
      expandColumn,
      expandFirstColumn,
      ...props.Columns.slice(1),
    ] as SCAdvanceTableProps<D>['Columns'];
  }, [props.Columns, Expanded, OnExpanded]);

  // flat
  const rows = useMemo(() => {
    if (!Expandable) {
      return props.Rows;
    }

    if (!props.Rows) {
      return undefined;
    }

    const newRows: D[] = [];

    function addRows(rows: D[], level: number) {
      rows.forEach((row) => {
        const rId = row[props.IdField];
        const isExpanded = Expanded.includes(rId);

        newRows.push({
          ...row,
          [treeLevelFieldName]: level,
        });

        if (isExpanded && row.children) {
          addRows(row.children, level + 1);
        }
      });
    }

    addRows(props.Rows, 0);

    return newRows;
  }, [props.Rows, Expanded]);

  return {
    ...rest,
    Columns: columns,
    Rows: rows,
  };
}

export { useTree };
